import {
  DropDownTypeItem,
  Maybe,
  SelectItem,
  SelectItemInt,
} from '@kistill/client/data-access'
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Grid,
  TextField,
} from '@mui/material'
import { useContext } from 'react'
import { Control } from 'react-hook-form'
import {
  RadioButtonGroup,
  SelectElement,
  TextFieldElement,
  CheckboxButtonGroup,
  AutocompleteElement,
} from 'react-hook-form-mui'
import { KistillCustomerSelector } from '../Form/KistillCustomerSelector'
import { KistillDatePicker } from '../Form/KistillDatePicker'
import { KistillLocationSelector } from '../Form/KistillLocationSelector'
import { KistillTagSelector } from '../Form/KistillTagSelector'
import { KistillTitleSelector } from '../Form/KistillTitleSelector'
import { KistillDiscLocationSelector } from '../Form/KistillDiscLocationSelector'
import { KistillColdLocationSelector } from '../Form/KistillColdLocationSelector'

interface InputProps {
  md?: number
}

export interface BaseFieldType {
  name: string
  label: string
  defaultValue?: string
  required?: boolean
  inputProps?: InputProps
  disabled?: boolean
  fullWidth?: boolean
}

interface TextFieldType extends BaseFieldType {
  type: 'text'
  multiline?: boolean
  minRows?: number
  maxRows?: number
}

interface TextFieldValueType extends BaseFieldType {
  type: 'text-field'
  value: string
}

interface DropdownFieldType extends BaseFieldType {
  type:
    | 'selectElement'
    | 'autocomplete'
    | 'titleSelector'
    | 'customerSelector'
    | 'locationSelector'
    | 'tagSelector'
    | 'discLocationSelector'
    | 'coldLocationSelector'
  value?: string
  onChange?: (value: string) => void
  options: {
    id: string
    title: string
  }[]
  multiple: boolean
  setSelectedLanguages?: React.Dispatch<React.SetStateAction<SelectItemInt[]>>
  setSelectedSubtitles?: React.Dispatch<React.SetStateAction<SelectItemInt[]>>
}

interface RadioFieldType extends BaseFieldType {
  type: 'radio'
  options: {
    id: string
    label: string
  }[]
  row?: boolean
}

interface CheckboxFieldType extends BaseFieldType {
  type: 'checkbox'
  options: {
    id: string
    label: string
  }[]
  control?: Control
  row?: boolean
}

interface DatepickerType extends BaseFieldType {
  type: 'datepicker'
}

export type FieldType =
  | TextFieldType
  | TextFieldValueType
  | DropdownFieldType
  | RadioFieldType
  | CheckboxFieldType
  | DatepickerType

interface FieldProps {
  field: FieldType
  DropDownTypeItemContext: React.Context<Maybe<DropDownTypeItem[]> | undefined>
  setOpenLocationPopup: (value: boolean) => void
  setSelectedLocations: (selectedLocation: SelectItem[]) => void
  itemTypeGuid: string | undefined
  setOpenTitlePopup: (value: boolean) => void
  setSelectedTitle: React.Dispatch<React.SetStateAction<SelectItem | undefined>>
  selectedTitle: SelectItem | undefined
  selectedContacts: SelectItem[]
  setSelectedContacts: (selectedContacts: SelectItem[]) => void
  setOpenCustomerPopup: (value: boolean) => void
  onCustomerChange: (
    _event: React.SyntheticEvent<Element, Event>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: unknown,
    _reason: AutocompleteChangeReason,
    _details?: AutocompleteChangeDetails<unknown> | undefined,
  ) => void
  dateRangeValidate: () => boolean | string
}
export const Field = (props: FieldProps) => {
  const { type } = props.field

  //Get the DropDownTypeItem Context
  const dropDownTypeItem = useContext(props.DropDownTypeItemContext)

  switch (type) {
    case 'text': {
      const { name, label, multiline, minRows, maxRows, inputProps } =
        props.field as TextFieldType

      return (
        <Grid item md={inputProps?.md}>
          <TextFieldElement
            name={name}
            label={label}
            multiline={multiline}
            minRows={minRows}
            maxRows={maxRows}
            inputProps={inputProps}
            fullWidth
          />
        </Grid>
      )
    }
    case 'text-field': {
      const { name, label, defaultValue, required, inputProps } =
        props.field as TextFieldValueType
      return (
        <Grid item md={inputProps?.md}>
          <TextField
            name={name}
            label={label}
            value={defaultValue}
            required={required}
            fullWidth
          />
        </Grid>
      )
    }
    case 'selectElement': {
      const { name, label, options, value, required, inputProps } =
        props.field as DropdownFieldType
      let dropDownOptions: SelectItemInt[] = []
      dropDownTypeItem?.filter((item) => {
        if (options.toString() === item.code) {
          dropDownOptions = item.dropDownItemResponse.map((dropDown) => {
            return {
              id: dropDown.id,
              title: dropDown.value,
            } as unknown as SelectItemInt
          })
          return dropDownOptions
        }
        return null
      })
      return (
        <Grid item md={inputProps?.md}>
          <SelectElement
            name={name}
            label={label}
            options={dropDownOptions}
            value={value}
            required={required}
            fullWidth
          />
        </Grid>
      )
    }
    case 'autocomplete': {
      const { name, label, options, inputProps, multiple } =
        props.field as DropdownFieldType

      let autocompleteOptions: SelectItemInt[] = []
      dropDownTypeItem?.filter((item) => {
        if (options.toString() === item.code) {
          autocompleteOptions = item.dropDownItemResponse.map((dropDown) => {
            return {
              id: dropDown.id,
              label: dropDown.value,
            } as unknown as SelectItemInt
          })
          return autocompleteOptions
        }
        return null
      })
      return (
        <Grid item md={inputProps?.md}>
          <AutocompleteElement
            multiple={multiple}
            name={`${name}`}
            label={label}
            options={autocompleteOptions}
          />
        </Grid>
      )
    }
    case 'radio': {
      const { name, label, options, row, required, inputProps } =
        props.field as RadioFieldType
      return (
        <Grid item md={inputProps?.md}>
          <RadioButtonGroup
            name={name}
            label={label}
            options={options}
            row={row}
            required={required}
          />
        </Grid>
      )
    }
    case 'datepicker': {
      const { name, label, inputProps } = props.field as DatepickerType
      return (
        <Grid item md={inputProps?.md}>
          <KistillDatePicker
            name={name}
            label={label}
            inputFormat='dd.MM.yyyy'
            inputProps={{ fullWidth: true }}
            isDate
            validation={{
              validate: props.dateRangeValidate,
            }}
          />
        </Grid>
      )
    }
    case 'checkbox': {
      const { name, options, row, label, control, inputProps } =
        props.field as CheckboxFieldType
      return (
        <Grid item md={inputProps?.md}>
          <CheckboxButtonGroup
            name={name}
            label={label}
            options={options}
            row={row}
            control={control}
          />
        </Grid>
      )
    }
    case 'titleSelector': {
      const { name, label, inputProps } = props.field as DropdownFieldType
      return (
        <Grid item md={inputProps?.md}>
          <KistillTitleSelector
            name={name}
            label={label}
            inputProps={{ fullWidth: true }}
            setOpenTitlePopup={props.setOpenTitlePopup}
            setSelectedTitle={props.setSelectedTitle}
            selectedTitle={props.selectedTitle}
          />
        </Grid>
      )
    }
    case 'customerSelector': {
      const { name, label, inputProps } = props.field as DropdownFieldType
      return (
        <Grid item md={inputProps?.md}>
          <KistillCustomerSelector
            name={name}
            label={label}
            setSelectedContacts={props.setSelectedContacts}
            selectedContacts={props.selectedContacts}
            setOpenCustomerPopup={props.setOpenCustomerPopup}
            onCustomerChange={props.onCustomerChange}
          />
        </Grid>
      )
    }
    case 'locationSelector': {
      const { name, label, inputProps, required } =
        props.field as DropdownFieldType
      return (
        <Grid item md={inputProps?.md}>
          <KistillLocationSelector
            name={name}
            label={label}
            inputProps={{ fullWidth: true }}
            setOpenLocationPopup={props.setOpenLocationPopup}
            setSelectedLocations={props.setSelectedLocations}
            itemTypeGuid={props.itemTypeGuid}
            required={required}
          />
        </Grid>
      )
    }
    case 'discLocationSelector': {
      const { name, label, inputProps } = props.field as DropdownFieldType
      return (
        <Grid item md={inputProps?.md}>
          <KistillDiscLocationSelector
            name={name}
            label={label}
            inputProps={{ fullWidth: true }}
          />
        </Grid>
      )
    }
    case 'tagSelector': {
      const { name, label, inputProps } = props.field as DropdownFieldType
      return (
        <Grid item md={inputProps?.md}>
          <KistillTagSelector
            name={name}
            label={label}
            inputProps={{ fullWidth: true }}
          />
        </Grid>
      )
    }
    case 'coldLocationSelector': {
      const { name, label, inputProps } = props.field as DropdownFieldType
      return (
        <Grid item md={inputProps?.md}>
          <KistillColdLocationSelector
            name={name}
            label={label}
            inputProps={{ fullWidth: true }}
            itemTypeGuid={props.itemTypeGuid}
          />
        </Grid>
      )
    }
    default:
      return null
  }
}
